import { useEffect, useRef, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircleIcon } from 'lucide-react';
import { PageHeader } from './ui/PageHeader';
import { useAdmin } from './AdminContext';
import { siteIssues } from '../data/site';
import { SalonSettings } from './settings/SalonSettings';
import { SalonDaysSettings } from './settings/SalonDaysSettings';
import { AppointmentSettings } from './settings/AppointmentSettings';
import { StaffSettings } from './settings/StaffSettings';
import { ShippingSettings } from './settings/ShippingSettings';
import { GiftSettings } from './settings/GiftSettings';
import { CodeSettings } from './settings/CodeSettings';
import { PressSettings } from './settings/PressSettings';
import { FeatureSettings } from './settings/FeatureSettings';
import { SeoSettings } from './settings/SeoSettings';
import { IntegrationSettings } from './settings/IntegrationSettings';
import { CostSettings } from './settings/CostSettings';
import { useRedirects, type RedirectsState } from './settings/useRedirects';
import { useCan } from './useCan';
import type { Permission } from '../core/types/team';
type TopicKey = 'salon' | 'team' | 'salondagen' | 'afspraken' | 'webshop' | 'cadeaus' | 'kosten' | 'vindbaarheid' | 'systeem';
type Go = (key: TopicKey) => void;
type Ctx = {
  go: Go;
  redirects: RedirectsState;
};
type Topic = {
  key: TopicKey;
  label: string;
  description: string;
  permission?: Permission;
  render: (ctx: Ctx) => ReactElement;
};

/** Negen onderwerpen, één tegelijk. De volgorde volgt hoe vaak Sarah er komt. */
const topics: Topic[] = [{
  key: 'salon',
  label: 'Salon & contact',
  description: 'Jij, de salon, contact en btw.',
  render: () => <SalonSettings data-id="element-4077" data-mp="l0fcyck2vtgz" />
}, {
  key: 'team',
  label: 'Team',
  description: 'Wie er behandelt, en wie mag inloggen.',
  render: ({
    go
  }) => <StaffSettings onOpenProfile={() => go('salon')} data-id="element-4078" data-mp="1rfpvrwyuq2p1" />
}, {
  key: 'salondagen',
  label: 'Salondagen',
  description: 'De vaste week en afwijkende dagen.',
  render: () => <SalonDaysSettings data-id="element-4079" data-mp="16rgiou9ri5op" />
}, {
  key: 'afspraken',
  label: 'Afspraken & kuren',
  description: 'Verzetten, kuren en het online consult.',
  render: () => <AppointmentSettings data-id="element-4080" data-mp="11ru5ulxuirgf" />
}, {
  key: 'webshop',
  label: 'Webshop & verzending',
  description: 'Verzending, ruilen en het abonnement.',
  render: () => <ShippingSettings data-id="element-4081" data-mp="1bqtfo7uckt50" />
}, {
  key: 'cadeaus',
  label: 'Cadeaus & codes',
  description: 'Cadeaus bij bestelling, cadeaubonnen en klantcodes.',
  render: ({
    go
  }) => <>
        <GiftSettings onOpenShipping={() => go('webshop')} data-id="element-4082" data-mp="nyw3gfmzjz3d" />
        <CodeSettings data-id="element-4083" data-mp="j3gqdpwsy780" />
      </>
}, {
  key: 'kosten',
  label: 'Kosten & marge',
  description: 'Betalen, verzenden en verpakking: wat er van een verkoop overblijft. Nooit zichtbaar op de site.',
  permission: 'finance.margin',
  render: () => <CostSettings data-id="element-4084" data-mp="5ahfy2bvn37b" />
}, {
  key: 'vindbaarheid',
  label: 'Vindbaarheid & pers',
  description: 'Sitemap, doorverwijzingen en pers.',
  render: ({
    redirects
  }) => <>
        <SeoSettings redirects={redirects} data-id="element-4085" data-mp="2byzswz6bim3a" />
        <PressSettings data-id="element-4086" data-mp="2by80tybxj6hr" />
      </>
}, {
  key: 'systeem',
  label: 'Koppelingen & privacy',
  description: 'Externe bronnen en wat er op de site aan staat.',
  render: () => <>
        <IntegrationSettings data-id="element-4087" data-mp="22251f08gplyi" />
        <FeatureSettings data-id="element-4088" data-mp="21yz8f9wppzxi" />
      </>
}];

/** Onbekend, leeg of niet toegestaan `?tab=`: het eerste onderwerp. */
const topicOf = (raw: string, list: Topic[]): TopicKey => list.find(t => t.key === raw)?.key ?? 'salon';

/**
 * De feiten van de zaak: één plek, en overal op de site klopt het. Links de onderwerpen, rechts
 * één onderwerp tegelijk. Alles hier gaat mee met de Opslaan-balk van het beheer, behalve wat er
 * zelf bij zegt dat het meteen gebeurt. Elk onderwerp staat in admin/settings/.
 */
export function Settings() {
  const {
    site,
    index
  } = useAdmin();
  const {
    canAll
  } = useCan();
  const visible = topics.filter(t => !t.permission || canAll(t.permission));
  const [params, setParams] = useSearchParams();
  const active = topicOf(params.get('tab') ?? '', visible);
  const topic = visible.find(t => t.key === active) ?? visible[0];
  const issues = siteIssues(site, id => Boolean(index.productById(id)));
  // Op paginaniveau: wisselen van onderwerp gooit niet-opgeslagen doorverwijzingen niet weg.
  const redirects = useRedirects();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const switched = useRef(false);
  const go: Go = key => {
    switched.current = true;
    setParams({
      tab: key
    }, {
      replace: true
    });
  };

  // Na wisselen: focus op de kop van het onderwerp (schermlezers), en terug naar boven.
  useEffect(() => {
    if (!switched.current) return;
    switched.current = false;
    headingRef.current?.focus({
      preventScroll: true
    });
    headingRef.current?.scrollIntoView({
      block: 'start'
    });
  }, [active]);
  return <div data-id="element-4089" data-mp="s5gjeoht2hxn">
      <PageHeader title="Instellingen" description="De feiten van je zaak. Pas je hier iets aan, dan klopt het overal: op de site, in de FAQ, de voorwaarden, je mails en in Google." data-id="element-4090" data-mp="s588311bzkok" />

      {issues.length > 0 && <div role="status" className="mb-6 rounded-lg border border-clay/30 bg-blush/40 px-5 py-4" data-id="element-4091" data-mp="33qz6c7c7vnq0">
          <p className="flex items-center gap-2 text-[14px] text-ink" data-id="element-4092" data-mp="38qlisek80zxr">
            <AlertCircleIcon className="h-4 w-4 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-4093" data-mp="38oild9gqo4j0" />
            Dit klopt nog niet
          </p>
          <ul className="mt-2 list-disc space-y-0.5 pl-10 text-[13px] text-muted" data-id="element-4094" data-mp="3igy1s2ce0l1s">
            {issues.map(i => <li key={i} data-id="element-4095" data-mp="3ndwv4io88nyx">{i}</li>)}
          </ul>
        </div>}

      <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-10" data-id="element-4096" data-mp="3rgl36o549quu">
        {/* Telefoon: een keuzelijst; desktop: het zijmenu. */}
        <label className="block lg:hidden" data-id="element-4097" data-mp="8fntwjcbw8e">
          <span className="sr-only" data-id="element-4098" data-mp="1x3lkgm5fwgjc">Onderwerp</span>
          <select value={active} onChange={e => go(topicOf(e.target.value, visible))} className="h-11 w-full rounded-md border border-line bg-white px-3 text-[15px] text-ink" data-id="element-4099" data-mp="1x4wpsqej9wck">
            {visible.map(t => <option key={t.key} value={t.key} data-id="element-4100" data-mp="30erakiqxrxsx">
                {t.label}
              </option>)}
          </select>
        </label>

        <nav aria-label="Onderwerpen" className="hidden lg:block" data-id="element-4101" data-mp="3eb4hwz1raito">
          <ul className="sticky top-20 space-y-0.5" data-id="element-4102" data-mp="39culkl4apc6h">
            {visible.map(t => {
            const current = t.key === active;
            return <li key={t.key} data-id="element-4103" data-mp="3jd3m2b5bljro">
                  <button type="button" onClick={() => go(t.key)} aria-current={current ? 'page' : undefined} className={`w-full rounded-md px-3 py-2 text-left text-[14px] transition-colors duration-150 ease-soft ${current ? 'bg-white text-ink shadow-sm ring-1 ring-line' : 'text-muted hover:bg-white/60 hover:text-ink'}`} data-id="element-4104" data-mp="3jc29u3ckir8d">
                    {t.label}
                  </button>
                </li>;
          })}
          </ul>
        </nav>

        <section aria-labelledby="instelling-kop" className="min-w-0 max-w-3xl" data-id="element-4105" data-mp="ui1yw8449ajt">
          <h2 id="instelling-kop" ref={headingRef} tabIndex={-1} className="scroll-mt-24 font-display text-[1.6rem] font-light text-ink outline-none" data-id="element-4106" data-mp="fk9m3npyf09b">
            {topic.label}
          </h2>
          <p className="mb-6 mt-1 text-[14px] text-muted" data-id="element-4107" data-mp="6spurmnnbsod">{topic.description}</p>
          <div key={topic.key} className="space-y-6" data-id="element-4108" data-mp="bk7t02yabf5n">
            {topic.render({
            go,
            redirects
          })}
          </div>
        </section>
      </div>
    </div>;
}